/* Borderline winter 26/27 · product card behaviour (P0, snippets/bl-product-card.liquid).
   - colour swatches: swap the main picture; the + then offers that colour's sizes
   - +: one size adds straight away; several sizes open the add layer (is-sizing), a size button adds it. The layer
     is built here on first use from the card's variant JSON — the server renders no size buttons (the home shelves
     carry ~200 cards)
   - adding goes through /cart/add.js and the theme's CartLinesUpdateEvent, so the cart bubble, the cart drawer
     and the drawer's auto-open setting behave exactly as with the product form
   - the heart keeps a small saved list in localStorage ('bl-wish') and tells the page through a 'bl:wish' event
   Cards are custom elements, so cards added later (shelf tabs, section rendering) wire themselves up. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const WISH_KEY = 'bl-wish';
const WISH_EVENT = 'bl:wish';
let layerCount = 0;

/** The saved list: [{ h: handle, t: title, v: vendor, u: url, i: image }] */
export const wishlist = {
  /** @returns {Array<{h: string, t: string, v: string, u: string, i: string}>} */
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => item && typeof item.h === 'string') : [];
    } catch (error) {
      return [];
    }
  },
  /** @param {Array<object>} list */
  write(list) {
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (error) {
      /* private mode or blocked storage: the heart still toggles for this page view */
    }
    document.dispatchEvent(new CustomEvent(WISH_EVENT, { detail: { count: list.length } }));
  },
  /** @param {string} handle */
  has(handle) {
    return this.read().some((item) => item.h === handle);
  },
  /** @param {{h: string, t: string, v: string, u: string, i: string}} entry */
  toggle(entry) {
    const list = this.read();
    const index = list.findIndex((item) => item.h === entry.h);
    if (index > -1) list.splice(index, 1);
    else list.unshift(entry);
    this.write(list);
    return index === -1;
  },
};

class BlProductCard extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #outside = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #doneTimer;

  /** @type {{ c: number, id: number[], a: boolean[], p: number[], o: Array<Array<string | null>> } | null | undefined} */
  #data;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener(WISH_EVENT, this.#syncWish, { signal });
    window.addEventListener('storage', this.#onStorage, { signal });

    this.#syncWish();
    this.#syncPlusLabel();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#outside?.abort();
    this.#outside = null;
    clearTimeout(this.#doneTimer);
  }

  /** @returns {HTMLElement | null} */
  get layer() {
    return this.querySelector('.pc__layer');
  }

  /** @returns {HTMLButtonElement | null} */
  get plus() {
    return this.querySelector('[data-bl-add]');
  }

  /** The card's variants, read once from its JSON (snippets/bl-product-card.liquid). */
  get data() {
    if (this.#data !== undefined) return this.#data;
    const script = this.querySelector('script[data-bl-variants]');
    try {
      const parsed = script ? JSON.parse(script.textContent || 'null') : null;
      this.#data = parsed && Array.isArray(parsed.id) ? parsed : null;
    } catch (error) {
      this.#data = null;
    }
    return this.#data;
  }

  /**
   * The variants of the colour the card shows (all of them without a colour choice), each with its button label:
   * the variant's other option values ("154 cm", "M", "Prizm Snow Sapphire").
   * @returns {Array<{ id: string, label: string, available: boolean }>}
   */
  variantsForColour() {
    const data = this.data;
    if (!data) return [];
    const colourIndex = typeof data.c === 'number' ? data.c : -1;
    const colour = this.dataset.colour;
    const columns = Array.isArray(data.o) ? data.o : [];
    /** @type {Array<{ id: string, label: string, available: boolean }>} */
    const list = [];
    data.id.forEach((id, i) => {
      const values = columns.map((column) => (Array.isArray(column) ? column[i] : null));
      if (colourIndex > -1 && colour && values[colourIndex] !== colour) return;
      const label =
        values.filter((value, k) => k !== colourIndex && value != null && value !== '').join(' / ') ||
        String(values[colourIndex] ?? '');
      list.push({ id: String(id), label, available: !Array.isArray(data.a) || data.a[i] !== false });
    });
    return list;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wishButton = target.closest('[data-bl-wish]');
    if (wishButton) return this.#toggleWish(/** @type {HTMLButtonElement} */ (wishButton));

    const swatch = target.closest('[data-bl-colour]');
    if (swatch instanceof HTMLElement) return this.selectColour(swatch.dataset.blColour || '');

    if (target.closest('[data-bl-close]')) return this.closeLayer(true);

    const add = target.closest('[data-bl-add]');
    if (add) return this.#onPlus();

    const option = target.closest('[data-bl-variant]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      this.closeLayer(false);
      this.addVariant(option.dataset.blVariant || '');
      this.plus?.focus({ preventScroll: true });
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-sizing')) {
      event.stopPropagation();
      this.closeLayer(true);
    }
  };

  #onPointerLeave = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse' && this.classList.contains('is-sizing') && window.innerWidth >= 990) {
      this.closeLayer(false);
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.classList.contains('is-sizing')) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (event.relatedTarget === null) return;
    // With the cart drawer open beside the page (≥ 990), its focus trap pulls every focus that lands on the page back
    // into the drawer, the size the + just focused included: that is not the shopper leaving the card. Pointer presses
    // elsewhere still close the layer (#onOutsidePointer); the page cannot take keyboard focus while the trap is on.
    const intoDrawer = event.relatedTarget instanceof Element && event.relatedTarget.closest('theme-drawer[open]');
    if (intoDrawer && !this.closest('theme-drawer')) return;
    this.closeLayer(false);
  };

  #onPlus() {
    const variants = this.variantsForColour();
    if (variants.length <= 1) {
      const only = variants[0];
      if (only && only.available) this.addVariant(only.id);
      else this.#flash(this.plus, false);
      return;
    }
    if (this.classList.contains('is-sizing')) {
      this.closeLayer(true);
      return;
    }
    document.querySelectorAll('bl-product-card.is-sizing').forEach((card) => {
      if (card !== this && card instanceof BlProductCard) card.closeLayer(false);
    });
    const layer = this.#buildLayer(variants);
    this.classList.add('is-sizing');
    this.plus?.setAttribute('aria-expanded', 'true');
    this.#outside?.abort();
    this.#outside = new AbortController();
    document.addEventListener('pointerdown', this.#onOutsidePointer, { signal: this.#outside.signal, capture: true });
    const first = /** @type {HTMLButtonElement | null} */ (layer.querySelector('[data-bl-variant]:not([disabled])'));
    first?.focus({ preventScroll: true });
  }

  /**
   * Build (once) and fill the add layer: a label, the close button, one button per variant of the shown colour.
   * @param {Array<{ id: string, label: string, available: boolean }>} variants
   * @returns {HTMLElement}
   */
  #buildLayer(variants) {
    let layer = this.layer;
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'pc__layer';
      layer.id = `bl-pc-layer-${++layerCount}`;
      layer.setAttribute('role', 'group');
      layer.setAttribute('aria-label', this.dataset.layerLabel || '');
      const head = document.createElement('div');
      head.className = 'hd';
      const label = document.createElement('span');
      label.className = 'lbl';
      label.textContent = this.dataset.layerLabel || '';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'pc__layer-x';
      close.dataset.blClose = '';
      close.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
      close.innerHTML = '<span aria-hidden="true">✕</span>';
      head.append(label, close);
      const options = document.createElement('div');
      options.className = 'opts';
      layer.append(head, options);
      this.querySelector('.pc__top')?.appendChild(layer);
      this.plus?.setAttribute('aria-controls', layer.id);
    }
    const options = /** @type {HTMLElement} */ (layer.querySelector('.opts'));
    options.dataset.n = String(variants.length);
    options.replaceChildren(
      ...variants.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = variant.available ? 'opt' : 'opt is-out';
        button.dataset.blVariant = variant.id;
        button.textContent = variant.label;
        if (!variant.available) {
          button.disabled = true;
          button.title = this.dataset.soldOut || '';
        }
        return button;
      })
    );
    return layer;
  }

  /** A press anywhere outside the card (the phone sheet included) closes the layer. */
  #onOutsidePointer = (/** @type {PointerEvent} */ event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.closeLayer(false);
  };

  /** @param {boolean} restoreFocus */
  closeLayer(restoreFocus) {
    this.#outside?.abort();
    this.#outside = null;
    if (!this.classList.contains('is-sizing')) return;
    this.classList.remove('is-sizing');
    this.plus?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.plus?.focus({ preventScroll: true });
  }

  /**
   * Turn the card to a colour: its picture, its pressed swatch, the sizes the + offers.
   * @param {string} colour
   */
  selectColour(colour) {
    this.dataset.colour = colour;
    for (const swatch of /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bl-colour]')])) {
      const on = swatch.dataset.blColour === colour;
      swatch.classList.toggle('is-on', on);
      swatch.setAttribute('aria-pressed', String(on));
      if (on && swatch.dataset.src) {
        const image = /** @type {HTMLImageElement | null} */ (this.querySelector('.pc__media img.main'));
        if (image) {
          image.srcset = swatch.dataset.srcset || '';
          image.src = swatch.dataset.src;
        }
      }
    }
    if (this.layer) this.#buildLayer(this.variantsForColour());
    this.#syncPlusLabel();
  }

  #syncPlusLabel() {
    const plus = this.plus;
    if (!plus) return;
    const count = this.variantsForColour().length;
    const label = count > 1 ? plus.dataset.labelChoose : plus.dataset.labelAdd;
    if (label) plus.setAttribute('aria-label', label);
    if (count <= 1) plus.removeAttribute('aria-expanded');
    else if (!plus.hasAttribute('aria-expanded')) plus.setAttribute('aria-expanded', 'false');
  }

  /**
   * Add one of a variant to the cart through the theme's cart event contract.
   * @param {string} variantId
   */
  async addVariant(variantId) {
    if (!variantId) return;
    const plus = this.plus;
    plus?.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((cartResponse) => cartResponse.json());

      const detail = {
        items: cart.items,
        source: 'bl-product-card',
        sourceId: this.dataset.productId,
        itemCount: 1,
        productId: this.dataset.productId,
      };

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, didError: true } });
        this.#flash(plus, false);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, sections: result.sections, didError: false },
      });
      this.#flash(plus, true);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#flash(plus, false);
    } finally {
      plus?.removeAttribute('aria-busy');
    }
  }

  /**
   * The + answers for 1.4s: ✓ when the line went in, ! when it did not.
   * @param {HTMLButtonElement | null} plus
   * @param {boolean} ok
   */
  #flash(plus, ok) {
    if (!plus) return;
    const glyph = plus.querySelector('span');
    clearTimeout(this.#doneTimer);
    plus.classList.add('is-done');
    if (glyph) glyph.textContent = ok ? '✓' : '!';
    this.#doneTimer = setTimeout(() => {
      plus.classList.remove('is-done');
      if (glyph) glyph.textContent = '+';
    }, 1400);
  }

  /** @param {HTMLButtonElement} button */
  #toggleWish(button) {
    const handle = this.dataset.handle || '';
    if (!handle) return;
    const on = wishlist.toggle({
      h: handle,
      t: this.dataset.title || '',
      v: this.dataset.vendor || '',
      u: this.dataset.url || '',
      i: this.dataset.image || '',
    });
    button.setAttribute('aria-pressed', String(on));
  }

  #syncWish = () => {
    const button = this.querySelector('[data-bl-wish]');
    if (button) button.setAttribute('aria-pressed', String(wishlist.has(this.dataset.handle || '')));
  };

  /** @param {StorageEvent} event */
  #onStorage = (event) => {
    if (event.key === WISH_KEY) this.#syncWish();
  };
}

if (!customElements.get('bl-product-card')) {
  customElements.define('bl-product-card', BlProductCard);
}

window.BL = window.BL || {};
window.BL.wishlist = wishlist;
